import { useState, Suspense } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useSubmitFeedback, useProfile } from "@/api";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FeedbackForm = ({ onClose }: { onClose: () => void }) => {
  const [message, setMessage] = useState("");
  const { data: profile } = useProfile();
  const submitFeedback = useSubmitFeedback();

  const handleSubmit = () => {
    if (!message.trim()) {
      toast.error("Please enter your feedback");
      return;
    }

    submitFeedback.mutate(
      {
        message: message.trim(),
        context: {
          url: window.location.href,
          userAgent: navigator.userAgent,
          userId: profile.id,
          email: profile.email,
          timestamp: new Date().toISOString(),
        },
      },
      {
        onSuccess: () => {
          toast.success("Thank you for your feedback!");
          setMessage("");
          onClose();
        },
        onError: () => {
          toast.error("Failed to submit feedback. Please try again.");
        },
      }
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Report Feedback</DialogTitle>
        <DialogDescription>
          Help us improve Notiflows by sharing your feedback, reporting bugs,
          or suggesting new features.
        </DialogDescription>
      </DialogHeader>
      <div className="py-4">
        <Textarea
          placeholder="Describe your feedback, bug report, or feature request..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={6}
          className="resize-none"
        />
        <p className="mt-3 text-xs text-muted-foreground">
          The following information will be included to help us investigate:
        </p>
        <div className="mt-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground font-mono">
          <div className="truncate">URL: {window.location.href}</div>
          <div className="truncate">User: {profile.email}</div>
          <div className="truncate">Browser: {navigator.userAgent.split(" ").slice(-2).join(" ")}</div>
        </div>
      </div>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={onClose}
          disabled={submitFeedback.isPending}
        >
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitFeedback.isPending}>
          {submitFeedback.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Submit Feedback
        </Button>
      </DialogFooter>
    </>
  );
};

const FeedbackFormFallback = () => (
  <div className="flex items-center justify-center py-12">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

export const FeedbackDialog = ({ open, onOpenChange }: FeedbackDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <Suspense fallback={<FeedbackFormFallback />}>
          <FeedbackForm onClose={() => onOpenChange(false)} />
        </Suspense>
      </DialogContent>
    </Dialog>
  );
};
